import { FC } from 'react';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/material/styles';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Switch from 'further-ui/components/Switch';
import { Control, Controller } from 'react-hook-form';
import { Column, Row } from 'further-ui/components/Layout';
import { PeriodEndReportFormValues } from '../schema';
import { ToggleableFieldConfig } from './types';

const LabelContainer = styled(Row)({
  flex: 4.875,
});

const InputsContainer = styled(Row)(({ theme }) => ({
  flex: 5.875,
  gap: theme.spacing(theme.spacingSizes.xl),
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(theme.spacingSizes.sm),
  },
}));

const ValueContainer = styled(Column)({
  flex: 4.875,
});

const SwitchContainer = styled(Column)(({ theme }) => ({
  flex: 1,
  [theme.breakpoints.down('sm')]: {
    flex: 'unset',
  },
}));

const FieldRow = styled(Row)(({ theme }) => ({
  width: '100%',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(theme.spacingSizes.xxs, theme.spacingSizes.xs),
  gap: theme.spacing(theme.spacingSizes.xl),
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(theme.spacingSizes.sm),
  },
}));

type Props = {
  config: ToggleableFieldConfig;
  control: Control<PeriodEndReportFormValues>;
  noInput?: boolean;
};

const ToggleableFieldRow: FC<Props> = ({ config, control, noInput }) => {
  return (
    <FieldRow centered breakpoint="sm">
      <LabelContainer centered spacing="xxs">
        <Typography variant="h6">{config.label}</Typography>
        {config.tooltip ? (
          <Tooltip title={config.tooltip}>
            <InfoOutlined fontSize="inherit" />
          </Tooltip>
        ) : null}
      </LabelContainer>
      <InputsContainer centered>
        {noInput ? null : (
          <ValueContainer>
            <Controller
              control={control}
              name={config.valuePath}
              render={({ field, fieldState }) =>
                config.readOnly ? (
                  <Typography variant="body2" color="grey.900">
                    {field.value}
                  </Typography>
                ) : (
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={config.placeholder}
                    value={field.value}
                    onChange={field.onChange}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )
              }
            />
          </ValueContainer>
        )}
        <SwitchContainer>
          <Controller
            control={control}
            name={config.includedPath}
            render={({ field }) => (
              <Switch
                checked={field.value}
                onChange={field.onChange}
                edge={noInput ? 'start' : undefined}
              />
            )}
          />
        </SwitchContainer>
      </InputsContainer>
    </FieldRow>
  );
};

export default ToggleableFieldRow;
